
import { Dispatch, SetStateAction, useCallback, useEffect, useRef, useState } from 'react'
import { HStack, IconButton } from '@chakra-ui/react'
import { GoogleMap, Marker, useJsApiLoader } from '@react-google-maps/api'
import { useVehicles } from 'context'

import { CompressIcon, ExpandIcon, LocationIcon } from 'components/SVGS'

import { customMapStyle } from './styles'

interface CustomMapsProps {
  height?: number | string
  isFullMap?: boolean
  setIsFullMap?: Dispatch<SetStateAction<boolean>>
}

const CustomMaps: React.FC<CustomMapsProps> = ({ height, setIsFullMap, isFullMap }) => {
  const {
    getVehicleLocation,
    vehicleState: { state: { vehicle, vehicleLocation: { latitude, longitude, orientation, timer } } }
  } = useVehicles()
  const [isMapManipulated, setIsMapManipulated] = useState<boolean>(false)

  const intervalRef = useRef<NodeJS.Timer>()
  const [currentLocation, setCurrentLocation] = useState({
    lat: latitude === 0 ? -33.4513 : latitude,
    lng: longitude === 0 ? -70.6653 : longitude
  })
  const [zoom, setZoom] = useState<number | null>(null)
  const [, setMap] = useState(null)
  const mapRef = useRef<GoogleMap | null>(null)
  const markerRef = useRef<Marker | null>(null)

  const { isLoaded } = useJsApiLoader({
    id: 'google-map-script',
    googleMapsApiKey: 'AIzaSyCylUCZh5qLuyg0qaY5dxpxT7hCtEoEneY',
    region: 'cl',
    language: 'es'
  })

  const position = {
    lat: latitude,
    lng: longitude
  }

  const center = {
    lat: latitude === 0 ? -33.4513 : latitude,
    lng: longitude === 0 ? -70.6653 : longitude
  }

  const handleCenter = () => {
    setZoom(18)
    setCurrentLocation({ lat: latitude, lng: longitude })
  }

  useEffect(() => {
    !isMapManipulated && handleCenter()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [latitude, longitude, orientation, isMapManipulated])

  useEffect(() => {
    if (vehicle && vehicle.isPremium && vehicle.id && timer > 1000) {
      intervalRef.current && clearInterval(intervalRef.current)
      intervalRef.current = setInterval(() => getVehicleLocation(vehicle.id, true), timer)
    } else
      intervalRef.current && clearInterval(intervalRef.current)

    return () => intervalRef.current && clearInterval(intervalRef.current)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [vehicle?.id, timer])

  const onLoad = useCallback(map => {
    const bounds = new window.google.maps.LatLngBounds(center)

    map.fitBounds(bounds)
    setMap(map)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const onUnmount = useCallback(() => {
    setMap(null)
  }, [])

  return (
    isLoaded
      ? <HStack justifyContent='flex-end' alignItems='flex-end' w='full' h='fit-content'>
        <GoogleMap
          ref={mapRef}
          center={currentLocation}
          onDrag={() => {
            !isMapManipulated && setIsMapManipulated(true)
            !isMapManipulated && setZoom(null)
          }}
          onClick={() => {
            !isMapManipulated && setIsMapManipulated(true)
            !isMapManipulated && setZoom(null)
          }}
          mapContainerStyle={{
            width: '100%',
            height: (typeof height === 'string' ? height : `${height ?? 200}px`),
            maxHeight: '100%',
            borderRadius: '22px',
          }}
          mapTypeId="roadmap"
          onLoad={onLoad}
          onUnmount={onUnmount}
          clickableIcons={false}
          options={{
            fullscreenControl: false,
            zoomControl: false,
            mapTypeControl: false,
            streetViewControl: false,
            controlSize: 0,
            styles: customMapStyle,
            zoom
          }}
        >
          <Marker
            ref={markerRef}
            key={'marker-1'}
            icon={{
              url: 'car-marker.png',
              anchor: new google.maps.Point(25, 25),
              scaledSize: new google.maps.Size(50, 50),
              rotation: orientation
            }}
            animation={google.maps.Animation.DROP}
            position={position}
          />
        </GoogleMap>
        <HStack pos='absolute' zIndex={2} flex={1} padding={4}>
          <IconButton
            aria-label=''
            role='group'
            onClick={() => setIsFullMap && setIsFullMap(!isFullMap)}
            variant={'icon'} icon={isFullMap ? <CompressIcon size={7} /> : <ExpandIcon size={7} />} />
          {isMapManipulated && <IconButton
            aria-label=''
            role='group'
            onClick={() => setIsMapManipulated(false)}
            variant={'icon'}
            icon={<LocationIcon size={7} />} />}
        </HStack>
      </HStack>
      : <></>
  )
}

export default CustomMaps
